import React, { useCallback, useEffect, useMemo, useState } from 'react'
import {
  Box, Button, Chip, CircularProgress, InputAdornment, Paper, Table,
  TableBody, TableCell, TableContainer, TableHead, TableRow, TextField, Typography,
} from '@material-ui/core'
import { makeStyles } from '@material-ui/core/styles'
import SearchIcon from '@material-ui/icons/Search'
import CloudDownloadIcon from '@material-ui/icons/CloudDownload'
import { useSearchParams } from 'react-router-dom'
import { api } from '../../api/client'
import Layout from '../../components/layout/Layout'
import { toFarsiDigits } from '../../assets/utils'

const thStyle = {
  backgroundColor: '#1a237e',
  color: '#fff',
  fontWeight: 'bold',
  fontSize: 12.5,
  whiteSpace: 'nowrap',
}

const money = (n) => toFarsiDigits(Number(n || 0).toLocaleString('en-US'))

const StatCard = ({ label, value, sub, gradient, shadow }) => (
  <div style={{
    borderRadius: 14, padding: '18px 20px', color: '#fff', position: 'relative',
    overflow: 'hidden', background: gradient, boxShadow: shadow,
  }}>
    <div style={{ fontSize: 12.5, opacity: 0.88 }}>{label}</div>
    <div style={{ fontSize: 23, fontWeight: 800, marginTop: 4, fontVariantNumeric: 'tabular-nums' }}>
      {value}
    </div>
    {sub ? <div style={{ fontSize: 11.5, opacity: 0.78, marginTop: 2 }}>{sub}</div> : null}
  </div>
)

const STAT_GRID = {
  display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(190px, 1fr))',
  gap: 14, marginBottom: 16,
}

const useStyles = makeStyles(() => ({
  periods: { display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginBottom: 14 },
  pill: {
    padding: '5px 14px', borderRadius: 999, border: '1px solid #d7dde7', fontSize: 12.5,
    fontWeight: 700, color: '#455a64', background: '#fff', cursor: 'pointer',
  },
  pillOn: { background: '#1a237e', borderColor: '#1a237e', color: '#fff' },
  toolbar: { display: 'flex', alignItems: 'center', gap: 12, marginBottom: 12, flexWrap: 'wrap' },
  count: { fontSize: 12.5, color: '#607d8b', fontWeight: 700 },
  wrap: { transition: 'opacity .15s ease' },
  dim: { opacity: 0.45 },
  sectionTitle: { fontWeight: 800, fontSize: 14.5, color: '#1a237e', margin: '20px 0 10px' },
  bar: { height: 7, borderRadius: 99, background: '#eceff1', overflow: 'hidden', minWidth: 70 },
  barIn: { height: '100%', background: 'linear-gradient(90deg,#ef6c00,#f9a825)' },
  orgGrid: {
    display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(255px,1fr))', gap: 12,
  },
  orgCard: {
    background: '#fff', border: '1px solid #e6eef2', borderRadius: 14, padding: '14px 16px',
    boxShadow: '0 2px 10px rgba(15,40,60,.05)', cursor: 'pointer',
    transition: 'transform .15s, box-shadow .15s, border-color .15s',
    '&:hover': {
      borderColor: '#0f6e7d', boxShadow: '0 6px 18px rgba(15,110,125,.15)', transform: 'translateY(-2px)',
    },
  },
  orgCardOn: {
    borderColor: '#0f6e7d', background: '#f2fbfc', boxShadow: '0 0 0 3px rgba(15,110,125,.12)',
  },
  orgTitle: { fontSize: 13.5, fontWeight: 800, color: '#0f172a', lineHeight: 1.6, marginBottom: 9 },
  orgNote: { fontSize: 11.5, color: '#64748b', marginTop: 8, fontWeight: 700 },
  pills: { display: 'flex', gap: 7, flexWrap: 'wrap', alignItems: 'center' },
  pillNormal: {
    borderRadius: 999, padding: '4px 12px', fontSize: 12, fontWeight: 800, whiteSpace: 'nowrap',
    background: '#e0f2fe', color: '#075985', border: '1px solid #7dd3fc',
  },
  pillSpecial: {
    borderRadius: 999, padding: '4px 12px', fontSize: 12, fontWeight: 800, whiteSpace: 'nowrap',
    background: '#fef3c7', color: '#92400e', border: '1px solid #fcd34d',
  },
  pillTotal: {
    borderRadius: 999, padding: '4px 12px', fontSize: 12, fontWeight: 800, whiteSpace: 'nowrap',
    background: '#f1f5f9', color: '#334155', border: '1px solid #cbd5e1',
  },
}))

const PERIODS = [
  { days: 30, title: '۱ ماه اخیر' },
  { days: 90, title: '۳ ماه اخیر' },
  { days: 180, title: '۶ ماه اخیر' },
  { days: 365, title: '۱ سال اخیر' },
]

export default function ContractorStats() {
  const classes = useStyles()
  const [searchParams, setSearchParams] = useSearchParams()
  const [days, setDays] = useState(90)
  const [data, setData] = useState(null)
  const [loading, setLoading] = useState(true)
  const [err, setErr] = useState('')
  const [q, setQ] = useState('')

  const deptParam = searchParams.get('dept') || ''

  const load = useCallback(async (d) => {
    setLoading(true)
    setErr('')
    try {
      const res = await api.get(`users/admin/contractor-stats/?days=${d}`)
      setData(res.data)
    } catch (e) {
      setErr('دریافت آمار حواله‌ها ناموفق بود')
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => { load(days) }, [days, load])

  const depts = (data && data.departments) || []
  const all = (data && data.contractors) || []

  // انتخاب سازمان از روی کارت‌ها (کلیک) یا از آدرس صفحه
  const selectDept = (id) => {
    const next = new URLSearchParams(searchParams)
    if (String(id) === deptParam) next.delete('dept')
    else next.set('dept', String(id))
    setSearchParams(next)
  }

  const activeDept = depts.find((d) => String(d.id) === deptParam) || null

  const filtered = useMemo(() => {
    const s = (q || '').trim().toLowerCase()
    return all
      .map((c) => {
        if (!deptParam) return { ...c, dName: '', dN: c.n, dAmount: c.amount }
        const hit = (c.departments || []).find((d) => String(d.id) === deptParam)
        if (!hit) return null
        return { ...c, dName: hit.name, dN: hit.n, dAmount: hit.amount }
      })
      .filter((c) => c !== null)
      .filter((c) => {
        if (!s) return true
        return (
          (c.name || '').toLowerCase().includes(s)
          || (c.national_code || '').toLowerCase().includes(s)
          || (c.mobile || '').toLowerCase().includes(s)
          || (c.departments || []).some((d) => (d.name || '').toLowerCase().includes(s))
        )
      })
  }, [all, q, deptParam])

  const exportCsv = () => {
    const head = ['نام پیمانکار', 'شناسه ملی', 'موبایل', 'تعداد حواله', 'جمع مبلغ (ریال)', 'آخرین حواله', 'سازمان']
    const lines = [head.join(',')]
    filtered.forEach((c) => {
      const dn = deptParam
        ? (c.dName || '')
        : (c.departments || []).map((d) => `${d.name} (${d.n})`).join(' | ')
      lines.push([
        `"${(c.name || '').replace(/"/g, "'")}"`,
        c.national_code || '',
        c.mobile || '',
        c.dN,
        c.dAmount,
        c.last || '',
        `"${String(dn).replace(/"/g, "'")}"`,
      ].join(','))
    })
    const blob = new Blob(['\uFEFF' + lines.join('\n')], { type: 'text/csv;charset=utf-8;' })
    const a = document.createElement('a')
    a.href = URL.createObjectURL(blob)
    a.download = `contractor-stats-${days}d.csv`
    a.click()
    URL.revokeObjectURL(a.href)
  }

  return (
    <Layout title="حوالهٔ پیمانکارانِ بی‌مشترک">
      <Box p={2} style={{ fontFamily: 'Vazir' }}>
        <Typography variant="body2" style={{ color: '#607d8b', marginBottom: 16 }}>
          پیمانکارانی که در بازهٔ انتخابی حواله داشته‌اند ولی هنوز مشترک نشده‌اند — روی هر سازمان بزنید تا فقط همان سازمان نمایش داده شود
        </Typography>

        {err ? (
          <Typography style={{ color: '#c62828', marginBottom: 12, fontSize: 13 }}>{err}</Typography>
        ) : null}

        <div className={classes.periods}>
          <span style={{ fontSize: 12.5, color: '#607d8b', fontWeight: 700 }}>بازهٔ آمار:</span>
          {PERIODS.map((p) => (
            <span
              key={p.days}
              className={`${classes.pill} ${days === p.days ? classes.pillOn : ''}`}
              onClick={() => setDays(p.days)}
            >
              {p.title}
            </span>
          ))}
          {loading ? <CircularProgress size={18} /> : null}
        </div>

        <div style={STAT_GRID}>
          <StatCard
            label="کل حواله‌های ثبت‌شده"
            value={toFarsiDigits((data && data.total_transfers) || 0)}
            sub={data && data.since ? `از ${data.since} تا ${data.today}` : ''}
            gradient="linear-gradient(135deg,#283593,#3949ab 60%,#5c6bc0)"
            shadow="0 16px 30px -20px rgba(57,73,171,.95)"
          />
          <StatCard
            label="⭐ پیمانکارانِ مشترک"
            value={toFarsiDigits((data && data.sub_count) || 0)}
            sub={`${toFarsiDigits((data && data.sub_transfers) || 0)} حواله — ${money((data && data.sub_amount) || 0)} ریال`}
            gradient="linear-gradient(135deg,#b45309,#f59e0b 60%,#fbbf24)"
            shadow="0 16px 30px -20px rgba(245,158,11,.95)"
          />
          <StatCard
            label="پیمانکارانِ غیر مشترک"
            value={toFarsiDigits((data && data.unsub_count) || 0)}
            sub={`${toFarsiDigits(((data && data.total_transfers) || 0) - ((data && data.sub_transfers) || 0))} حواله — هدف فروش اشتراک`}
            gradient="linear-gradient(135deg,#334155,#475569 60%,#64748b)"
            shadow="0 16px 30px -20px rgba(71,85,105,.95)"
          />
          <StatCard
            label="سازمان‌های درگیر"
            value={toFarsiDigits(depts.length)}
            sub={`جمع مبالغ: ${money((data && data.total_amount) || 0)} ریال`}
            gradient="linear-gradient(135deg,#00695c,#00897b 60%,#26a69a)"
            shadow="0 16px 30px -20px rgba(0,137,123,.95)"
          />
        </div>

        <div className={classes.sectionTitle}>۱) تعداد حواله به تفکیک سازمان</div>
        <div className={classes.orgGrid}>
          {depts.map((d) => (
            <div
              key={d.id}
              className={`${classes.orgCard} ${String(d.id) === deptParam ? classes.orgCardOn : ''}`}
              onClick={() => selectDept(d.id)}
              title={`نمایش پیمانکاران بی‌مشترک ${d.name}`}
            >
              <div className={classes.orgTitle}>{d.name}</div>
              <div className={classes.pills}>
                <span className={classes.pillNormal}>حواله {toFarsiDigits(d.n)}</span>
                {d.sub_contractors > 0 ? (
                  <span className={classes.pillSpecial}>⭐ مشترک {toFarsiDigits(d.sub_contractors)}</span>
                ) : null}
                <span className={classes.pillTotal}>غیر مشترک {toFarsiDigits(d.unsub_contractors)}</span>
              </div>
              <div className={classes.orgNote}>
                {toFarsiDigits(d.sub_transfers)} حوالهٔ مشترک · {toFarsiDigits(d.unsub_transfers)} حوالهٔ غیر مشترک
              </div>
            </div>
          ))}
        </div>

        <div className={classes.sectionTitle}>۲) فهرست پیمانکارانِ بی‌مشترک</div>
        <div className={classes.toolbar}>
          <TextField
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="نام، موبایل، کد ملی یا سازمان…"
            variant="outlined"
            size="small"
            style={{ minWidth: 280, flex: 1 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" style={{ color: '#90a4ae' }} />
                </InputAdornment>
              ),
            }}
          />
          {activeDept ? (
            <Chip
              label={`سازمان: ${activeDept.name}`}
              onDelete={() => selectDept(activeDept.id)}
              size="small"
              style={{ background: '#e0f2fe', color: '#075985', fontWeight: 800 }}
            />
          ) : null}
          <span className={classes.count}>
            {toFarsiDigits(filtered.length)} از {toFarsiDigits(all.length)} پیمانکار
          </span>
          <Button
            size="small" variant="outlined" startIcon={<CloudDownloadIcon />} onClick={exportCsv}
            disabled={!filtered.length}
          >
            خروجی اکسل (CSV)
          </Button>
        </div>

        <div className={`${classes.wrap} ${loading ? classes.dim : ''}`}>
          <TableContainer component={Paper} elevation={2}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  {['#', 'نام پیمانکار', 'موبایل', 'کد ملی', 'تعداد حواله', 'جمع مبلغ (ریال)', 'آخرین حواله', 'سازمان'].map((h) => (
                    <TableCell key={h} align="center" style={thStyle}>{h}</TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {filtered.length === 0 && !loading && (
                  <TableRow>
                    <TableCell colSpan={8} align="center" style={{ padding: 28, color: '#90a4ae' }}>
                      پیمانکاری یافت نشد
                    </TableCell>
                  </TableRow>
                )}
                {filtered.map((c, i) => (
                  <TableRow key={c.id} hover>
                    <TableCell align="center">{toFarsiDigits(i + 1)}</TableCell>
                    <TableCell style={{ fontWeight: 700 }}>{c.name}</TableCell>
                    <TableCell align="center" dir="ltr">{c.mobile || '—'}</TableCell>
                    <TableCell align="center" dir="ltr">{c.national_code || '—'}</TableCell>
                    <TableCell align="center" style={{ fontWeight: 800, color: '#ef6c00' }}>
                      {toFarsiDigits(c.dN)}
                    </TableCell>
                    <TableCell align="center">{money(c.dAmount)}</TableCell>
                    <TableCell align="center">{c.last || '—'}</TableCell>
                    <TableCell align="center">
                      {deptParam ? (
                        <span style={{ fontSize: 12, fontWeight: 700, color: '#1565c0' }}>{c.dName}</span>
                      ) : (
                        (c.departments || []).map((d) => (
                          <Chip
                            key={d.id} size="small" label={`${d.name} (${toFarsiDigits(d.n)})`}
                            onClick={() => selectDept(d.id)}
                            style={{ margin: 2, background: '#e3f2fd', color: '#1565c0', fontWeight: 700, fontSize: 11, cursor: 'pointer' }}
                          />
                        ))
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </div>
      </Box>
    </Layout>
  )
}
